import { useEffect, useState } from 'react';
import { dashboardApi, ConversionParityResponse } from '../services/dashboardApi';

const NETWORK_LABELS: Record<string, string> = {
    'adtraction': 'Adtraction',
    'salus': 'Salus',
    'cake': 'SmartResponse (CAKE)',
    'has-offers': 'HasOffers',
    'linktrust': 'Orion / LinkTrust',
    'addrevenue': 'AddRevenue',
};

const HASOFFERS_ACCOUNTS: Record<string, string> = {
    '16728': 'Axo',
    'api': 'Axo (API only)',
};

const fmt = (n: number | null | undefined, digits = 0) =>
    n === null || n === undefined ? '–' : Number(n).toLocaleString('da-DK', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const coverageClass = (pct: number | null) => {
    if (pct === null) return 'text-gray-400';
    if (pct >= 99) return 'text-green-600';
    if (pct >= 95) return 'text-amber-600';
    return 'text-red-600';
};

/**
 * WCT phase-out panel: shows, per network, how many WeCanTrack transactions are also
 * seen by the direct network API sync (and vice versa). Cutover per network when
 * coverage stays >= 99% for 14 days.
 */
export default function WctParityPanel() {
    const [days, setDays] = useState(14);
    const [data, setData] = useState<ConversionParityResponse | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [showSamples, setShowSamples] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError('');
        dashboardApi.getConversionParity(days)
            .then(setData)
            .catch((e: any) => setError(e.message || 'Failed to load parity'))
            .finally(() => setLoading(false));
    }, [days]);

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">WCT vs. direkte API (parity)</h3>
                    <p className="text-xs text-gray-500">
                        Dækning = andel af WCT-transaktioner som også ses via netværkets eget API. Cutover pr. netværk ved ≥ 99% i 14 dage.
                    </p>
                </div>
                <select
                    className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg p-2"
                    value={days}
                    onChange={(e) => setDays(parseInt(e.target.value))}
                >
                    <option value={7}>7 dage</option>
                    <option value={14}>14 dage</option>
                    <option value={30}>30 dage</option>
                    <option value={90}>90 dage</option>
                </select>
            </div>

            {loading && <div className="text-sm text-gray-400 py-4">Henter…</div>}
            {error && <div className="text-sm text-red-600 py-2">{error}</div>}

            {data && !loading && (
                <>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-gray-500 border-b">
                                    <th className="py-2 pr-4">Netværk</th>
                                    <th className="py-2 pr-4 text-right">Dækning</th>
                                    <th className="py-2 pr-4 text-right">Begge</th>
                                    <th className="py-2 pr-4 text-right">Kun WCT</th>
                                    <th className="py-2 pr-4 text-right">Kun API</th>
                                    <th className="py-2 pr-4 text-right">WCT DKK</th>
                                    <th className="py-2 pr-4 text-right">API DKK</th>
                                    <th className="py-2 pr-4 text-right">Status-afvigelser</th>
                                    <th className="py-2 pr-4">Sidste API-sync</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.summary.map((r) => {
                                    const label = NETWORK_LABELS[r.network_id] || r.network_id;
                                    const acct = r.account ? ` · ${HASOFFERS_ACCOUNTS[r.account] || 'konto ' + r.account}` : '';
                                    return (
                                        <tr key={`${r.network_id}-${r.account}`} className={`border-b last:border-0 ${r.has_api_sync ? '' : 'opacity-60'}`}>
                                            <td className="py-2 pr-4 font-medium text-gray-700">
                                                {label}{acct}
                                                {!r.has_api_sync && <span className="ml-2 text-xs text-gray-400">(ingen API-sync)</span>}
                                            </td>
                                            <td className={`py-2 pr-4 text-right font-semibold ${coverageClass(r.coverage_pct)}`}>
                                                {r.coverage_pct === null ? '–' : `${fmt(r.coverage_pct, 1)}%`}
                                            </td>
                                            <td className="py-2 pr-4 text-right">{fmt(r.matched)}</td>
                                            <td className="py-2 pr-4 text-right">{fmt(r.wct_only)}</td>
                                            <td className="py-2 pr-4 text-right">{fmt(r.api_only)}</td>
                                            <td className="py-2 pr-4 text-right">{fmt(r.wct_revenue_dkk)}</td>
                                            <td className="py-2 pr-4 text-right">{fmt(r.api_revenue_dkk)}</td>
                                            <td className="py-2 pr-4 text-right">{fmt(r.status_mismatch)}</td>
                                            <td className="py-2 pr-4 text-xs text-gray-500">
                                                {r.last_api_seen ? new Date(r.last_api_seen).toLocaleString('da-DK') : '–'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <button
                        className="mt-3 text-xs text-indigo-600 hover:underline"
                        onClick={() => setShowSamples((s) => !s)}
                    >
                        {showSamples ? 'Skjul' : 'Vis'} umatchede transaktioner ({data.unmatched_samples.length})
                    </button>
                    {showSamples && (
                        <div className="overflow-x-auto mt-2">
                            <table className="min-w-full text-xs">
                                <thead>
                                    <tr className="text-left text-gray-500 border-b">
                                        <th className="py-1 pr-3">Side</th>
                                        <th className="py-1 pr-3">Netværk</th>
                                        <th className="py-1 pr-3">Transaktion</th>
                                        <th className="py-1 pr-3">Annoncør</th>
                                        <th className="py-1 pr-3">Tid</th>
                                        <th className="py-1 pr-3 text-right">DKK</th>
                                        <th className="py-1 pr-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.unmatched_samples.map((s) => (
                                        <tr key={`${s.network_id}-${s.transaction_id}`} className="border-b last:border-0">
                                            <td className="py-1 pr-3">{s.side === 'wct_only' ? 'Kun WCT' : 'Kun API'}</td>
                                            <td className="py-1 pr-3">{NETWORK_LABELS[s.network_id] || s.network_id}</td>
                                            <td className="py-1 pr-3 font-mono">{s.transaction_id}</td>
                                            <td className="py-1 pr-3">{s.advertiser_name}</td>
                                            <td className="py-1 pr-3">{new Date(s.timestamp).toLocaleString('da-DK')}</td>
                                            <td className="py-1 pr-3 text-right">
                                                {fmt(s.side === 'wct_only' ? s.wct_revenue_dkk : s.api_revenue_dkk, 2)}
                                            </td>
                                            <td className="py-1 pr-3">{s.side === 'wct_only' ? s.status : s.api_status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
